/**
 * Utility for detecting kids/children TV categories by name.
 *
 * Covers 8 languages (EN, PT, ES, FR, DE, IT, TR, AR) and common IPTV
 * naming patterns such as "Canais | Infantis", "Kids", "Kinderprogramm", etc.
 * Matching is done after stripping diacritics so "niños", "çocuk", "criança"
 * all resolve correctly regardless of the server's encoding.
 */

/** Keywords that, when found anywhere in a category name, classify it as kids content. */
const KIDS_KEYWORDS: string[] = [
  // English
  'kids',
  'children',
  'child',
  'cartoon',
  'family',
  // Portuguese (BR) / Spanish — "infantil", "infantis", "infantiles", "criança" → "crianca"
  'infant',
  'crianca',
  // Spanish — "niños", "niñas" → "ninos"/"ninas"
  'nino',
  'nina',
  // French
  'enfant',
  'jeunesse',
  // German — "kinder", "kinderserie", "kindergarten"
  'kinder',
  // Italian
  'bambini',
  'ragazzi',
  // Turkish — "çocuk" → "cocuk", "çocuklar" → "cocuklar"
  'cocuk',
  // Arabic
  'أطفال',
];

/**
 * Strips Latin diacritics so accented letters match their base ASCII equivalent.
 * Arabic characters are preserved as-is.
 */
function normalize(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip combining diacritical marks
    .toLowerCase();
}

/**
 * Returns true if the given category name (from an Xtream/IPTV server) appears
 * to be a kids / children category based on multilingual keyword matching.
 */
export function isKidsCategory(categoryName: string): boolean {
  if (!categoryName) return false;

  const normalized = normalize(categoryName);

  // Preserve Arabic check on original (lowercased) since it doesn't need NFD normalization
  const lower = categoryName.toLowerCase();

  return KIDS_KEYWORDS.some((kw) => {
    if (kw === 'أطفال') return lower.includes(kw);
    return normalized.includes(kw);
  });
}
